import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { User } from '@/features/auth/types/user';
import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { useGetProjectOwnershipTransferByToken } from '@/features/projects/project-details/api/get-project-ownership-transfer-by-token';
import {
  claimProjectOwnership,
  ProjectOwnershipTransferByToken,
} from '@/features/projects/project-details/api/project-ownership-transfer';
import {
  FlowLayout,
  FlowLayoutBody,
} from '@/shared/components/flow/flow-layout';
import { Background } from '@/shared/components/layouts/background';
import { LogoWithName } from '@/shared/components/theme/logo-with-name';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Spinner } from '@/shared/components/ui/spinner';
import { toast } from '@/shared/hooks/use-toast';
import { Org } from '@/shared/types/api';
import { getInitials } from '@/shared/utils/avatar-util';

interface InvitationError {
  title: string;
  message: string;
}

interface InvitationCardProps {
  title: string;
  children: React.ReactNode;
  isError?: boolean;
}

const InvitationCard = ({
  title,
  children,
  isError = false,
}: InvitationCardProps) => {
  return (
    <Card variant={'styled'}>
      <CardHeader className="bg-muted/20">
        <CardTitle
          className={`text-center ${isError ? 'text-destructive' : ''}`}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 pt-6">
        {children}
      </CardContent>
    </Card>
  );
};

const ErrorCard = ({ title, message }: InvitationError) => {
  const navigate = useNavigate();
  const { t } = useTranslation('project', {
    keyPrefix: 'claim_project_ownership',
  });

  return (
    <InvitationCard title={title} isError>
      <p className="text-center text-destructive">{message}</p>
      <Button onClick={() => navigate(paths.home.getHref())} variant="outline">
        {t('go_home')}
      </Button>
    </InvitationCard>
  );
};

const LoadingCard = () => {
  const { t } = useTranslation('project', {
    keyPrefix: 'claim_project_ownership',
  });

  return (
    <Card variant={'styled'} wrapWithStars>
      <CardHeader className="items-center space-y-14 pt-14">
        <CardTitle className="text-4xl font-medium">
          {t('processing_title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <Spinner className="size-12" />
        <p className="text-center text-xl">{t('processing_message')}</p>
      </CardContent>
    </Card>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Background>
      <div className="fixed left-10 top-10">
        <LogoWithName />
      </div>
      <FlowLayout>
        <FlowLayoutBody>{children}</FlowLayoutBody>
      </FlowLayout>
    </Background>
  );
};

const ClaimActions = ({
  transfer,
  invitationToken,
  orgs,
}: {
  transfer: ProjectOwnershipTransferByToken;
  invitationToken: string;
  orgs: Org[];
}) => {
  const navigate = useNavigate();
  const { t: sharedT } = useTranslation('shared');
  const { t } = useTranslation('project', {
    keyPrefix: 'claim_project_ownership',
  });
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  const { mutate: claim, isPending } = useMutation({
    mutationFn: async () => {
      const result = await claimProjectOwnership({
        invitationToken,
        orgId: parseInt(selectedOrgId),
      });
      return result as number;
    },
    onSuccess: (projectId: number) => {
      toast({ title: t('success') });
      navigate(paths.projects.details.base.getHref(projectId.toString()));
    },
    onError: () => {
      toast({ title: t('error'), variant: 'destructive' });
    },
  });

  const handleClaim = () => {
    if (!selectedOrgId) return;
    claim();
  };

  const handleDecline = () => {
    navigate(paths.home.getHref());
  };

  return (
    <Card variant={'styled'} wrapWithStars>
      <CardHeader className="items-center space-y-14 pt-14">
        <Avatar className="size-24">
          <AvatarFallback>
            {getInitials(transfer.project_name || '')}
          </AvatarFallback>
        </Avatar>

        <CardTitle className="text-4xl font-medium">{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <p className="text-center text-xl">
          <Trans
            ns="project"
            i18nKey="claim_project_ownership.description"
            values={{
              inviter_name: transfer.inviter_name || t('unknown_name'),
              inviter_email: transfer.inviter_email || '',
              project_name: transfer.project_name,
            }}
          />
        </p>
        <p className="text-center text-xl">{t('prompt')}</p>

        <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
          <SelectTrigger className="w-40 max-w-xs md:w-80">
            <SelectValue
              asLabelInput={false}
              placeholder={t('select_org_placeholder')}
            />
          </SelectTrigger>
          <SelectContent>
            {orgs.map((org) => (
              <SelectItem key={org.id} value={org.id.toString()}>
                {org.name ?? sharedT('no_name')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
      <CardFooter className="mt-14 justify-center gap-12 pb-14">
        <Button onClick={handleDecline} variant="outline" className="w-40">
          {t('cancel_button')}
        </Button>
        <Button
          onClick={handleClaim}
          variant="greenGradient"
          className="w-40"
          disabled={!selectedOrgId || isPending}
        >
          {isPending ? <Spinner className="size-4" /> : t('claim_button')}
        </Button>
      </CardFooter>
    </Card>
  );
};

export const ClaimProjectOwnershipPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('project', {
    keyPrefix: 'claim_project_ownership',
  });
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');

  const { data: user, isLoading: isUserLoading } = useUser();
  const { data: orgs, isLoading: isOrgsLoading } = useUserOrgs();
  const { data: transfer, isLoading: isTransferLoading } =
    useGetProjectOwnershipTransferByToken({
      invitationToken: invitationToken || '',
      enabled: !!invitationToken && !!user,
    });

  useEffect(() => {
    if (!user && !isUserLoading) {
      const redirectPath = `/claim-project-ownership?token=${invitationToken}`;
      navigate(paths.auth.login.getHref(redirectPath));
    }
  }, [user, isUserLoading, invitationToken, navigate]);

  useEffect(() => {
    if (transfer?.status === 'accepted') {
      navigate(paths.home.getHref());
    }
  }, [transfer, navigate]);

  if (!invitationToken) {
    return (
      <Layout>
        <ErrorCard title={t('error_title')} message={t('missing_token')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Body
        isUserLoading={isUserLoading}
        isOrgsLoading={isOrgsLoading}
        isTransferLoading={isTransferLoading}
        transfer={transfer}
        user={user}
        orgs={orgs}
        invitationToken={invitationToken}
      />
    </Layout>
  );
};

const Body = ({
  isUserLoading,
  isOrgsLoading,
  isTransferLoading,
  transfer,
  user,
  orgs,
  invitationToken,
}: {
  isUserLoading: boolean;
  isOrgsLoading: boolean;
  isTransferLoading: boolean;
  transfer: ProjectOwnershipTransferByToken | null | undefined;
  user: User | null | undefined;
  orgs: Org[] | undefined;
  invitationToken: string;
}) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'claim_project_ownership',
  });

  if (isUserLoading || isTransferLoading || isOrgsLoading) {
    return <LoadingCard />;
  }

  if (!transfer) {
    return <ErrorCard title={t('error_title')} message={t('fetch_error')} />;
  }

  if (transfer.status === 'accepted') {
    return (
      <ErrorCard title={t('error_title')} message={t('already_accepted')} />
    );
  }

  if (transfer.status === 'cancelled') {
    return (
      <ErrorCard title={t('error_title')} message={t('transfer_cancelled')} />
    );
  }

  if (user?.email !== transfer.to_email) {
    return <ErrorCard title={t('error_title')} message={t('wrong_user')} />;
  }

  if (!orgs?.length) {
    return <ErrorCard title={t('error_title')} message={t('fetch_error')} />;
  }

  return (
    <ClaimActions
      invitationToken={invitationToken}
      transfer={transfer}
      orgs={orgs}
    />
  );
};
